import React, { useEffect, useRef } from "react";
import { ArrowRight, Phone, Star, ShieldCheck, Sparkles } from "lucide-react";
import { company, images, stats } from "../mock";

export default function Hero() {
  const wrapRef = useRef(null);
  const cardRef = useRef(null);
  const imgRef = useRef(null);

  useEffect(() => {
    const onMove = (e) => {
      if (!wrapRef.current) return;
      const rect = wrapRef.current.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width - 0.5;
      const y = (e.clientY - rect.top) / rect.height - 0.5;
      if (cardRef.current) {
        cardRef.current.style.transform = `rotateY(${x * 8}deg) rotateX(${-y * 8}deg) translateZ(0)`;
      }
      if (imgRef.current) {
        imgRef.current.style.transform = `translate(${x * -18}px, ${y * -12}px) scale(1.05)`;
      }
    };
    const el = wrapRef.current;
    if (el) el.addEventListener("mousemove", onMove);
    return () => { if (el) el.removeEventListener("mousemove", onMove); };
  }, []);

  return (
    <section id="home" className="relative min-h-[100vh] pt-28 pb-16 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0" style={{ background: "linear-gradient(160deg, #030524 0%, #070B5F 55%, #0F1780 100%)" }} />
      <div className="absolute inset-0 opacity-30" style={{
        backgroundImage: `radial-gradient(circle at 20% 20%, rgba(141,198,63,0.35) 0%, transparent 40%), radial-gradient(circle at 80% 80%, rgba(26,36,160,0.55) 0%, transparent 45%)`
      }} />
      <div className="orb" style={{ width: 380, height: 380, background: "#6BA82E", top: -80, right: -80 }} />
      <div className="orb" style={{ width: 340, height: 340, background: "#1A24A0", bottom: -100, left: -80, opacity: 0.7 }} />

      <div ref={wrapRef} className="relative max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center parallax-wrap">
        {/* Left copy */}
        <div className="relative z-10 text-white">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-6 border border-white/15" style={{ background: "rgba(255,255,255,0.06)" }}>
            <Sparkles size={14} style={{ color: "#8DC63F" }} />
            <span className="text-xs tracking-wider uppercase text-white/85">Now serving TX · LA · FL</span>
          </div>

          <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl font-semibold leading-[1.02] tracking-tight">
            You made us your <span className="text-gold-grad">Top Choice.</span>
            <br />
            <span className="text-white/90">We made you our </span>
            <span className="text-gold-grad">Top Priority.</span>
          </h1>

          <p className="mt-6 text-lg text-white/75 max-w-xl leading-relaxed">
            Residential and commercial mortgage lending built around your goals.
            From first-time buyers to seasoned investors — we shop dozens of lenders
            to secure your best rate, fast.
          </p>

          <div className="mt-9 flex flex-wrap items-center gap-4">
            <a href={company.applyUrl} target="_blank" rel="noreferrer" className="btn-gold pulse-gold">
              Apply Now <ArrowRight size={16} />
            </a>
            <a href="#calculator" className="btn-navy" style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.18)" }}>
              Try the Calculator
            </a>
            <a href={`tel:${company.phoneRaw}`} className="flex items-center gap-2 text-white/85 hover:text-white text-sm font-medium ml-2">
              <Phone size={16} />
              {company.phone}
            </a>
          </div>

          <div className="mt-10 flex items-center gap-6 text-white/70 text-sm">
            <div className="flex items-center gap-2">
              <ShieldCheck size={16} style={{ color: "#8DC63F" }} />
              Licensed Mortgage Company
            </div>
            <div className="flex items-center gap-1">
              <Star size={14} className="fill-current" style={{ color: "#8DC63F" }} />
              <Star size={14} className="fill-current" style={{ color: "#8DC63F" }} />
              <Star size={14} className="fill-current" style={{ color: "#8DC63F" }} />
              <Star size={14} className="fill-current" style={{ color: "#8DC63F" }} />
              <Star size={14} className="fill-current" style={{ color: "#8DC63F" }} />
              <span className="ml-1">4.9 / 5</span>
            </div>
          </div>
        </div>

        {/* Right — 3D floating card */}
        <div className="relative h-[520px] hidden lg:block">
          <div
            ref={cardRef}
            className="absolute inset-0 rounded-3xl overflow-hidden shadow-3d-lg tilt-card"
            style={{ transformOrigin: "center" }}
          >
            <img
              ref={imgRef}
              src={images.hero}
              alt="Luxury home"
              className="w-full h-full object-cover transition-transform duration-500"
            />
            <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, transparent 40%, rgba(3,5,36,0.75) 100%)" }} />

            {/* Floating rate card */}
            <div className="absolute bottom-6 left-6 right-6 glass rounded-2xl p-5 shadow-3d tilt-inner">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500">30-Yr Fixed • From</div>
                  <div className="font-serif text-3xl font-bold" style={{ color: "#070B5F" }}>6.49%*</div>
                  <div className="text-xs text-slate-500 mt-1">APR 6.62% • Rates as of today</div>
                </div>
                <a href="#calculator" className="btn-outline">Estimate</a>
              </div>
            </div>
          </div>

          {/* Floating stat chip */}
          <div className="absolute -top-4 -left-4 glass rounded-2xl px-4 py-3 shadow-3d flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "#070B5F" }}>
              <Star size={18} className="fill-current" style={{ color: "#8DC63F" }} />
            </div>
            <div className="leading-tight">
              <div className="text-xs text-slate-500">Loans Closed</div>
              <div className="font-serif font-bold text-lg" style={{ color: "#070B5F" }}>$1.2B+</div>
            </div>
          </div>

          <div className="absolute -bottom-6 -right-4 glass rounded-2xl px-4 py-3 shadow-3d">
            <div className="text-xs text-slate-500">Avg. Close Time</div>
            <div className="font-serif font-bold text-lg" style={{ color: "#070B5F" }}>21 days</div>
          </div>
        </div>
      </div>

      {/* Stats strip */}
      <div className="relative max-w-7xl mx-auto px-6 mt-16">
        <div className="glass-dark rounded-2xl px-6 py-6 grid grid-cols-2 md:grid-cols-4 gap-4">
          {stats.map((s) => (
            <div key={s.label} className="text-center border-r border-white/10 last:border-r-0 md:px-4">
              <div className="font-serif text-3xl md:text-4xl font-bold stat-num" style={{ color: "#8DC63F" }}>{s.value}</div>
              <div className="text-white/70 text-xs md:text-sm mt-1 tracking-wide uppercase">{s.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
